import { Card, Div, Flex, FormStatus, HelpButton, P } from '@dnb/eufemia';
import { DialogContentProps, DialogProps } from '@dnb/eufemia/components/dialog/types';
import { Form } from '@dnb/eufemia/extensions/forms';
import { CurrentApiAndScopeEntitlementsDto } from '@portals/shared/portal/AppApiEntitlementDto';
import { ApiError } from '@portals/shared-frontend/utils/ApiError';
import { type JSX, useCallback, useId, useMemo, useRef, useState } from 'react';

import { updateClient } from '@/api/app';
import Toast from '@/components/Toast';

import ScopeCard from '../../../../../ScopeCard';
import { CrqField } from '../../../CrqField';
import { ApiScopeEntitlementCard } from '../ApiScopeEntitlementCard/ApiScopeEntitlementCard';
import { busyEditingTooltip, crqHelp, crqLabelDescription } from '../DetailsSection';
import SubmitConfirmationSummary from '../SubmitConfirmationSummary';

export type AccessesFormData = {
  crq?: string;
  selectedScopes: Record<string, boolean>;
};

const accessesHelp = (
  <HelpButton left="x-small" size="small" title="Accesses">
    <Div top="small">
      The scopes below are the entitlements this app has on its attached APIs. By default a client inherits all scopes
      available in its environment, but you can narrow it down to a specific subset.
    </Div>
  </HelpButton>
);

type Props = {
  appId: string;
  availableApiAndScopeEntitlements: CurrentApiAndScopeEntitlementsDto[];
  canEdit: boolean;
  clientId: string;
  description: string;
  editDisabled: boolean;
  extraScopesInClient: string[];
  initialScopes: string[];
  liveMode: boolean;
  name: string;
  onEdit: () => void;
  onEditEnd: () => void;
  onSaved: () => Promise<unknown>;
  selectedScopes: Record<string, boolean>;
};

export default function AccessesSection({
  appId,
  availableApiAndScopeEntitlements,
  canEdit,
  clientId,
  description,
  editDisabled,
  extraScopesInClient,
  initialScopes,
  liveMode,
  name,
  onEdit,
  onEditEnd,
  onSaved,
  selectedScopes,
}: Props): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string>();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  // Settled when the user confirms or declines the diff dialog, so the async
  // onDone can stay pending (section in edit mode) until they decide.
  const confirmResolver = useRef<{ resolve: () => void; reject: () => void } | null>(null);

  const formId = `update-client-accesses-${useId()}`;

  const grantedScopesByApi = useMemo(() => {
    return availableApiAndScopeEntitlements
      .map(({ api, scopeEntitlements }) => ({
        apiName: api.name,
        scopes: scopeEntitlements
          .filter(
            ({ liveMode: scopeLiveMode, scopeName }) => scopeLiveMode === liveMode && initialScopes.includes(scopeName),
          )
          .toSorted((a, b) => a.scopeName.localeCompare(b.scopeName)),
      }))
      .filter(({ scopes }) => scopes.length > 0)
      .toSorted((a, b) => a.apiName.localeCompare(b.apiName));
  }, [availableApiAndScopeEntitlements, initialScopes, liveMode]);

  const handleDone = useCallback(async () => {
    const { data } = Form.getData<AccessesFormData>(formId);
    const scopes = Object.entries(data.selectedScopes ?? {})
      .filter(([, isSelected]) => isSelected)
      .map(([scopeName]) => scopeName);

    // The CRQ field is not `required` (that would trap Cancel, whose restored
    // value is always empty), so guard it here. Throwing keeps the section open.
    if (liveMode && !data.crq) {
      setErrorMessage('A ServiceNow CRQ is required to change a client in the live environment.');
      throw new Error('Missing CRQ');
    }

    // Confirm the scope diff before saving. The promise stays pending - so the
    // section stays in edit mode - until the user acts on the dialog. Declining
    // rejects it, which keeps the section open with the selection intact.
    await new Promise<void>((resolve, reject) => {
      confirmResolver.current = { resolve, reject };
      setConfirmationOpen(true);
    });

    try {
      setErrorMessage(undefined);
      await updateClient(appId, clientId, { description, name, scopes }, liveMode ? data.crq : undefined);
      await onSaved();
      onEditEnd();
      setSuccessMessage('Client accesses updated');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      if (ApiError.isApiError(error)) {
        setErrorMessage(error.body.message);
      } else {
        setErrorMessage('An unexpected error occurred');
      }
      // Rejecting keeps the section in edit mode with the selection intact.
      throw error;
    }
  }, [appId, clientId, description, formId, liveMode, name, onEditEnd, onSaved]);

  const connectWithDialog = useMemo<
    Pick<DialogProps & DialogContentProps, 'onClose' | 'onConfirm' | 'onDecline' | 'open'>
  >(() => {
    const settle = (outcome: 'resolve' | 'reject') => {
      setConfirmationOpen(false);
      confirmResolver.current?.[outcome]();
      confirmResolver.current = null;
    };
    return {
      onClose: () => settle('reject'),
      onConfirm: () => settle('resolve'),
      onDecline: () => settle('reject'),
      open: confirmationOpen,
    };
  }, [confirmationOpen]);

  const hasGrantedScopes = grantedScopesByApi.length > 0 || extraScopesInClient.length > 0;

  return (
    <Form.Handler<AccessesFormData>
      defaultData={{ crq: undefined, selectedScopes }}
      id={formId}
      locale="en-GB"
      translations={{ 'en-GB': { SectionEditContainer: { doneButton: 'Save' } } }}
    >
      {successMessage && <Toast message={successMessage} position="bottom" variant="success" />}
      <Form.Section containerMode="view" disableEditing={!canEdit}>
        <Form.Section.ViewContainer title={<>Accesses{accessesHelp}</>}>
          {hasGrantedScopes ? (
            <Flex.Stack gap="medium" wrap={false}>
              {grantedScopesByApi.map(({ apiName, scopes }) => (
                <Card key={apiName}>
                  <P bottom="x-small" weight="medium">
                    {apiName}
                  </P>
                  {scopes.map(({ scopeDescription, scopeId, scopeName }, index) => (
                    <ScopeCard
                      environments={[]}
                      key={scopeId || scopeName}
                      scopeDescription={scopeDescription}
                      scopeId={scopeId}
                      scopeName={scopeName}
                      showAccessSection={false}
                      showDivider={index < scopes.length - 1}
                    />
                  ))}
                </Card>
              ))}
              {extraScopesInClient.length > 0 && (
                <Card>
                  <P bottom="x-small" weight="medium">
                    Extra scopes
                  </P>
                  {extraScopesInClient
                    .toSorted((a, b) => a.localeCompare(b))
                    .map((scopeName) => (
                      <ScopeCard
                        environments={[]}
                        key={scopeName}
                        scopeDescription="Granted on the client but not entitled to the app."
                        scopeId={scopeName}
                        scopeName={scopeName}
                        showAccessSection={false}
                      />
                    ))}
                </Card>
              )}
            </Flex.Stack>
          ) : (
            <P>This client has no accesses.</P>
          )}
          {canEdit && (
            <Form.Section.Toolbar onEdit={onEdit}>
              <Form.Section.ViewContainer.EditButton
                disabled={editDisabled}
                tooltip={editDisabled ? busyEditingTooltip : undefined}
              />
            </Form.Section.Toolbar>
          )}
        </Form.Section.ViewContainer>
        <Form.Section.EditContainer title={<>Edit accesses{accessesHelp}</>}>
          <ApiScopeEntitlementCard
            availableApiAndScopeEntitlements={availableApiAndScopeEntitlements}
            extraScopesInClient={extraScopesInClient}
            isLiveClient={liveMode}
          />
          {liveMode && (
            <CrqField customHelp={crqHelp} labelDescription={crqLabelDescription} path="/crq" required={false} />
          )}
          <Form.Visibility animate visible={!!errorMessage}>
            <FormStatus state="error" stretch text={errorMessage} top="small" />
          </Form.Visibility>
          {/* Own toolbar so Cancel skips the "discard your changes?" confirm. */}
          <Form.Section.Toolbar onCancel={onEditEnd} onDone={handleDone}>
            <Form.Section.EditContainer.DoneButton />
            <Form.Section.EditContainer.CancelButton showConfirmDialog={false} />
          </Form.Section.Toolbar>
        </Form.Section.EditContainer>
      </Form.Section>
      <SubmitConfirmationSummary
        allEntitlements={availableApiAndScopeEntitlements}
        connectWithDialog={connectWithDialog}
        formId={formId}
        initalExtraScopes={extraScopesInClient}
        initialScopes={initialScopes}
        isLiveMode={liveMode}
      />
    </Form.Handler>
  );
}
